/** 輕量 Markdown 渲染器(AI 生成內容顯示用)。
 *  自 public/js/markdown.js 移植。
 *
 *  安全策略:輸入先整段 HTML escape,再以白名單語法轉換,無 XSS 通道。
 */

export function escapeHtml(s: unknown): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** 放進 href 的網址一律經過這裡:只允許 http(s)、站內絕對路徑與錨點。
 *
 *  escapeHtml 只做 HTML 實體跳脫,不看 URL scheme —— `javascript:fetch(...)`
 *  經過它之後仍是一個點下去就會執行的連結。外部資源匯入的 url 由呼叫端的
 *  request body 提供(後端未驗 scheme),會存進共用的 kb_documents,再被其他
 *  角色——包含管理員——在自己的 session 下點開,構成教師→管理員的提權路徑。
 */
export function safeHref(url: unknown): string {
  const s = String(url ?? '').trim();
  return /^(https?:\/\/|\/(?!\/)|#)/i.test(s) ? escapeHtml(s) : '#';
}

/** LaTeX 命令對照表(AI 筆記常含公式,以純文字/上下標近似呈現,不依賴外部渲染套件) */
const GREEK: Record<string, string> = {
  alpha: 'α', beta: 'β', gamma: 'γ', Gamma: 'Γ', delta: 'δ', Delta: 'Δ',
  epsilon: 'ε', theta: 'θ', lambda: 'λ', mu: 'μ', nu: 'ν', pi: 'π',
  rho: 'ρ', sigma: 'σ', Sigma: 'Σ', tau: 'τ', phi: 'φ', omega: 'ω', Omega: 'Ω',
};

function mathToPlain(tex: string): string {
  let t = String(tex);
  t = t.replace(/\\frac\{([^{}]*)\}\{([^{}]*)\}/g, '($1/$2)');
  t = t.replace(/\\(?:text|mathrm|mathbf)\{([^{}]*)\}/g, '$1');
  t = t.replace(/\^\\circ/g, '°');
  t = t.replace(/\^\{([^{}]+)\}/g, '<sup>$1</sup>');
  t = t.replace(/\^([0-9A-Za-z])/g, '<sup>$1</sup>');
  t = t.replace(/_\{([^{}]+)\}/g, '<sub>$1</sub>');
  t = t.replace(/_([0-9A-Za-z])/g, '<sub>$1</sub>');
  t = t.replace(
    /\\(alpha|beta|gamma|Gamma|delta|Delta|epsilon|theta|lambda|mu|nu|pi|rho|sigma|Sigma|tau|phi|omega|Omega)\b/g,
    (_m, name: string) => GREEK[name] ?? name,
  );
  t = t.replace(/\\cdot/g, '·').replace(/\\times/g, '×').replace(/\\pm/g, '±')
    .replace(/\\leq/g, '≤').replace(/\\geq/g, '≥').replace(/\\neq/g, '≠')
    .replace(/\\infty/g, '∞').replace(/\\circ/g, '°').replace(/\\,/g, ' ')
    .replace(/\\left|\\right/g, '').replace(/\\\\/g, ' ');
  t = t.replace(/\\([a-zA-Z]+)/g, '$1'); // 未知指令保留名稱,去掉反斜線
  t = t.replace(/[{}]/g, '');
  return t.trim();
}

/** 行內語法:`code`、**粗體**、*斜體*、[text](url)(僅 http/https)、$..$/$$..$$ 公式 */
function inline(s: string): string {
  const codes: string[] = [];
  // 行內 code 先抽出,避免內容被粗斜體規則改寫
  let out = s.replace(/`([^`]+)`/g, (_m, c: string) => {
    codes.push(c);
    return ` ${codes.length - 1} `;
  });

  const maths: string[] = [];
  out = out.replace(/\$\$([^$]+)\$\$|\$([^$\n]+)\$/g,
    (_m, block: string | undefined, inlineExpr: string | undefined) => {
      maths.push(mathToPlain(block !== undefined ? block : (inlineExpr ?? '')));
      return `${maths.length - 1}`;
    });

  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/(^|[^*])\*([^*\s][^*]*)\*/g, '$1<em>$2</em>');
  out = out.replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener">$1</a>');
  out = out.replace(/&lt;br\s*\/?&gt;/gi, '<br>');
  out = out.replace(/(\d+)/g,
    (_m, i: string) => `<span class="ekm-math">${maths[Number(i)] ?? ''}</span>`);
  out = out.replace(/ (\d+) /g,
    (_m, i: string) => `<code>${codes[Number(i)] ?? ''}</code>`);
  return out;
}

const isTableRow = (line: string) => /^\s*\|.*\|\s*$/.test(line);

const isTableSep = (line: string) =>
  /^\s*\|?[\s:-]*-[\s:|-]*\|?\s*$/.test(line)
  && line.includes('-')
  && (line.match(/\|/g) || []).length >= 1;

function splitRow(line: string): string[] {
  return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|')
    .map((c) => c.trim());
}

export function render(md: string): string {
  const lines = escapeHtml(md).split(/\r?\n/);
  const out: string[] = [];
  let para: string[] = [];        // 段落緩衝
  const listStack: string[] = []; // 巢狀清單 ['ul','ol']

  function flushPara(): void {
    if (para.length) {
      out.push(`<p>${para.map(inline).join('<br>')}</p>`);
      para = [];
    }
  }
  function closeLists(toDepth: number): void {
    while (listStack.length > toDepth) out.push(`</${listStack.pop()}>`);
  }
  function flushAll(): void {
    flushPara();
    closeLists(0);
  }

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i] ?? '';

    // ``` fenced code block(內容不做行內處理)
    if (/^\s*```/.test(line)) {
      flushAll();
      const buf: string[] = [];
      i += 1;
      while (i < lines.length && !/^\s*```/.test(lines[i] ?? '')) {
        buf.push(lines[i] ?? '');
        i += 1;
      }
      out.push(`<pre><code>${buf.join('\n')}</code></pre>`);
      continue;
    }

    // 標題 # ~ ######
    const h = line.match(/^\s*(#{1,6})\s+(.+)$/);
    if (h) {
      flushAll();
      const lv = (h[1] ?? '').length;
      out.push(`<h${lv}>${inline((h[2] ?? '').trim())}</h${lv}>`);
      continue;
    }

    // 分隔線
    if (/^\s*(-{3,}|\*{3,})\s*$/.test(line)) {
      flushAll();
      out.push('<hr>');
      continue;
    }

    // 表格(表頭列 + 分隔列)
    if (isTableRow(line) && i + 1 < lines.length && isTableSep(lines[i + 1] ?? '')) {
      flushAll();
      const head = splitRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && isTableRow(lines[i] ?? '')) {
        rows.push(splitRow(lines[i] ?? ''));
        i += 1;
      }
      i -= 1;
      out.push('<table><thead><tr>'
        + head.map((c) => `<th>${inline(c)}</th>`).join('')
        + '</tr></thead><tbody>'
        + rows.map((r) => `<tr>${r.map((c) => `<td>${inline(c)}</td>`).join('')}</tr>`).join('')
        + '</tbody></table>');
      continue;
    }

    // 清單(- / * / 1. ,支援一層縮排巢狀)
    const li = line.match(/^(\s*)([-*]|\d+\.)\s+(.+)$/);
    if (li) {
      flushPara();
      const depth = (li[1] ?? '').length >= 2 ? 2 : 1;
      const tag = /\d/.test(li[2] ?? '') ? 'ol' : 'ul';
      while (listStack.length > depth) out.push(`</${listStack.pop()}>`);
      while (listStack.length < depth) {
        out.push(`<${tag}>`);
        listStack.push(tag);
      }
      if (listStack[listStack.length - 1] !== tag) {
        out.push(`</${listStack.pop()}>`);
        out.push(`<${tag}>`);
        listStack.push(tag);
      }
      out.push(`<li>${inline(li[3] ?? '')}</li>`);
      continue;
    }

    // 引用
    const q = line.match(/^\s*&gt;\s?(.*)$/);
    if (q) {
      flushAll();
      const qbuf = [q[1] ?? ''];
      while (i + 1 < lines.length && /^\s*&gt;\s?/.test(lines[i + 1] ?? '')) {
        i += 1;
        qbuf.push((lines[i] ?? '').replace(/^\s*&gt;\s?/, ''));
      }
      out.push(`<blockquote><p>${qbuf.map(inline).join('<br>')}</p></blockquote>`);
      continue;
    }

    // 空行 → 段落結束
    if (!line.trim()) {
      flushAll();
      continue;
    }

    // 一般文字聚成段落
    closeLists(0);
    para.push(line);
  }

  flushAll();
  return out.join('\n');
}
